import React, { useState, useEffect } from "react";
import { Typography, Box } from "@mui/material";
import { Category, StaffMember, ProductFormData } from "../types/productTypes";
import { formatAmount, removeEmojis } from "../utils/formatters";
import FormTextField from "../../../../components/FormTextField";
import FormSelect from "../../../../components/FormSelect";
import CategorySelect from "./CategorySelect";
import StaffSelect from "./StaffSelect";
import ImageUpload from "./ImageUpload";
import { useBusinessTheme } from "src/shared/hooks/useBusinessTheme";
import LoadingScreen from "src/shared/components/LoadingScreen";
import FormCheckbox from "src/shared/components/FormCheckbox";
import { ModalWrapper } from "src/shared/components/ModalWrapper";
import CommonButtons from "src/shared/components/CommonButtons";
import { useUser } from "src/business-types/grocery/components/UserContext";
import { normalizeFoodTypes } from "src/shared/utils/normalizeFoodTypes";

interface AddProductModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: ProductFormData) => Promise<void> | void;
  categories: Category[];
  staffMembers: StaffMember[];
  businessType: string;
  loading?: boolean;
  initialData?: Partial<ProductFormData>;
  addProductError?: string;
}

const AddProductModal: React.FC<AddProductModalProps> = ({
  open,
  onClose,
  onSubmit,
  categories,
  staffMembers,
  businessType,
  loading = false,
  initialData,
  addProductError = "",
}) => {
  useBusinessTheme();

  const initialFormState: ProductFormData = {
    productName: initialData?.productName || "",
    arabicName: initialData?.arabicName || "",
    amount: initialData?.amount || "",
    categoryId: initialData?.categoryId || "",
    type: initialData?.type || "",
    staffList: initialData?.staffList || [],
    image: (initialData?.image as File | null) ?? null,
    ownProduct: initialData?.ownProduct || false,
  };
  
  const { userConfigration } = useUser();
  
  // Normalize food types from user configuration
  const foodTypes = userConfigration?.foodTypes 
    ? normalizeFoodTypes(userConfigration.foodTypes)
    : ["Veg", "Non-Veg"];
  
  const [formData, setFormData] = useState<ProductFormData>(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    productName?: string;
    arabicName?: string;
  }>({});

  const getCategoryInitials = (categoryName: string): string => {
    const words = categoryName.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].substring(0, 5).toUpperCase();
    } else {
      return words.map((word) => word[0].toUpperCase()).join("");
    }
  };

  useEffect(() => {
    if (open) {
      setFormData(initialFormState);
      setErrors({});
    }
  }, [open]);

  const setField = (field: keyof ProductFormData, value: any) => {
    setFormData((prev) => {
      const newFormData = { ...prev, [field]: value };

      // When category changes, update product name with category initials
      if (field === "categoryId" && value) {
        const selectedCategory = categories.find((cat) => cat._id.$oid === value);
        if (selectedCategory && newFormData.productName) {
          const categoryInitials = getCategoryInitials(selectedCategory.CategoryName);
          const nameWithoutOldInitials = newFormData.productName.replace(/\[\w+\]\s*$/, "").trim();
          newFormData.productName = `${nameWithoutOldInitials} [${categoryInitials}]`;
        }
      }

      return newFormData;
    });

    if (errors[field as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const isFormValid = () => {
    const amountValue = parseFloat(formData.amount);
    return formData.productName.trim() !== "" && formData.arabicName.trim() !== "" && !isNaN(amountValue) && 
    amountValue > 0 && formData.categoryId.trim() !== "";
  };

  const handleSubmit = async () => {
    if (!isFormValid() || isSubmitting) return;

    try {
      setIsSubmitting(true);
      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProductNameChange = (value: string) => {
    const cleanedValue = removeEmojis(value);
    setField("productName", cleanedValue);
  };

  const handleProductNameBlur = () => {
    let cleanedValue = formData.productName.trim();

    if (formData.categoryId) {
      const selectedCategory = categories.find((cat) => cat._id.$oid === formData.categoryId);
      if (selectedCategory) {
        const categoryInitials = getCategoryInitials(selectedCategory.CategoryName);
        const initialsPattern = new RegExp(`\\[${categoryInitials}\\]$`);

        if (!initialsPattern.test(cleanedValue)) {
          const nameWithoutInitials = cleanedValue.replace(/\[\w+\]\s*$/, "").trim();
          cleanedValue = `${nameWithoutInitials} [${categoryInitials}]`;
        }
      }
    }

    setField("productName", cleanedValue);
  };

  if (!open) return null;

  return (
    <ModalWrapper title='Add Product' onClose={onClose} width='max-w-xl'>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* ✅ Show backend validation error */}
        {addProductError && (
          <Typography color='error' variant='body2' sx={{ mb: 2, textAlign: "center" }}>
            {addProductError}
          </Typography>
        )}

        <FormTextField
          id='productName'
          label='Product name'
          required
          value={formData.productName}
          onChange={(e) => handleProductNameChange(e.target.value)}
          onBlur={handleProductNameBlur}
          error={!!errors.productName}
          helperText={errors.productName}
        />

        <FormTextField
          id='arabicName'
          label='Arabic name'
          required
          value={formData.arabicName}
          onChange={(e) => setField("arabicName", removeEmojis(e.target.value))}
          sx={{ direction: "ltr" }}
          error={!!errors.arabicName}
          helperText={errors.arabicName}
        />

        <FormTextField
          id='amount'
          label='Product price'
          required
          type='number'
          value={formData.amount}
          onChange={(e) => setField("amount", e.target.value)}
          onBlur={() => {
            // Only format if there's a value and it's not "0"
            if (formData.amount && formData.amount !== "0") {
            setField("amount", formatAmount(formData.amount));
            }
            // If it's "0", keep it as "0"
          }}
          error={formData.amount === "0" || parseFloat(formData.amount) < 0}
          helperText={formData.amount === "0" ? "Price must be greater than 0" : parseFloat(formData.amount) < 0 ? "Price cannot be negative"  : "" }
        />

        {businessType === "Restaurant" && (
          <FormSelect
            id='type'
            label='Type'
            value={formData.type}
            onChange={(e) => setField("type", e.target.value)}
            options={foodTypes.map((type: string) => ({
              value: type,
              label: type,
            }))}
          />
        )}

        <CategorySelect categories={categories} value={formData.categoryId} onChange={(v) => setField("categoryId", v)} />

        {businessType === "Saloon" && (
          <StaffSelect staffMembers={staffMembers} value={formData.staffList} onChange={(v) => setField("staffList", v)} />
        )}

        <ImageUpload value={formData.image ?? null} onChange={(file) => setField("image", file)} />

        <FormCheckbox label='Its Inward Product' checked={formData.ownProduct} onChange={(_, c) => setField("ownProduct", c)} />

        {/* Buttons */}
        <div className='flex justify-end gap-3 mt-6'>
          <CommonButtons className='w-32 h-10' label='Cancel' variant='outlineDanger' onClick={onClose} />
          <CommonButtons
            className='w-32 h-10'
            label={isSubmitting || loading ? <LoadingScreen /> : "Confirm"}
            variant='primary'
            onClick={handleSubmit}
            disabled={!isFormValid() || isSubmitting || loading}
          />
        </div>
      </Box>
    </ModalWrapper>
  );
};

export default AddProductModal;